/**
 * Unanswered Duty Call offer hold — the Dispatch policy that closes an offer nobody answered.
 * Stored as seconds on `account_duty_tracking_policies`, edited in minutes, and bounded 60-3600
 * by the same CHECK the duty ping interval uses.
 *
 * The hold is Dispatch's clock, not the officer's: Guard shows no countdown and no "goes back to
 * Dispatch" sentence. Nothing in this module is imported by the mobile app.
 */

export const OFFER_HOLD_MIN_SECONDS = 60;
export const OFFER_HOLD_MAX_SECONDS = 3600;

export const OFFER_HOLD_PRESETS = [
  { label: "1 min", seconds: 60 },
  { label: "2 min", seconds: 120 },
  { label: "5 min", seconds: 300 },
  { label: "10 min", seconds: 600 },
  { label: "15 min", seconds: 900 },
] as const;

export function offerHoldSecondsToMinutes(seconds: number): number {
  return Math.round(seconds / 60);
}

export function isOfferHoldSecondsInRange(seconds: number): boolean {
  return (
    Number.isInteger(seconds)
    && seconds >= OFFER_HOLD_MIN_SECONDS
    && seconds <= OFFER_HOLD_MAX_SECONDS
  );
}

/** Plain-language hold length for the policy summary and the picker's live echo. */
export function formatOfferHold(seconds: number): string {
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  }
  return `${seconds} seconds`;
}

/**
 * What the selected Call's live offer can honestly say about its hold.
 *
 * `elapsed` is deliberately not "expired". Expiry is expire-on-touch: the row is still `offered`
 * until the next Dispatch or Guard action on that account runs the private helper, so claiming the
 * offer is already gone would be a statement the database has not made yet.
 */
export interface OfferHoldRemaining {
  elapsed: boolean;
  label: string;
}

function twoDigits(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * Remaining hold on one live offer. Null when the stamp is absent or unreadable — the caller then
 * renders no hold line at all rather than inventing a clock. Null is also the right answer when
 * the account policy is off, which the caller checks before ever calling this.
 */
export function offerHoldRemaining(
  expiresAtIso: string | null,
  nowMs: number,
): OfferHoldRemaining | null {
  if (expiresAtIso === null) return null;
  const expiresMs = Date.parse(expiresAtIso);
  if (!Number.isFinite(expiresMs) || !Number.isFinite(nowMs)) return null;
  const remainingSeconds = Math.ceil((expiresMs - nowMs) / 1000);
  if (remainingSeconds <= 0) {
    return {
      elapsed: true,
      label: "Hold elapsed. This offer closes on the next Dispatch or officer action.",
    };
  }
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return { elapsed: false, label: `Hold remaining ${minutes}:${twoDigits(seconds)}` };
}
